import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { notificationApi, request } from '../services/cloudApi';
import { useAuth } from './AuthContext';
import i18n from '../i18n';
import renderNotification from '../utils/renderNotification';

const getNotificationsStorageKey = (userId) =>
  userId ? `notesplus_notifications_${userId}` : null;

const getAdminBellStorageKey = (userId) =>
  userId ? `notesplus_admin_bell_${userId}` : null;

const getNotificationsFromStorage = (userId) => {
  const key = getNotificationsStorageKey(userId);
  if (!key) return [];
  try {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : [];
  } catch { return []; }
};

const saveNotificationsToStorage = (notifications, userId) => {
  const key = getNotificationsStorageKey(userId);
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify(notifications));
  } catch {}
};

const getAdminBellFromStorage = (userId) => {
  const key = getAdminBellStorageKey(userId);
  if (!key) return [];
  try {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : [];
  } catch { return []; }
};

const NotificationContext = createContext(null);

// Banner'da kalması gereken yerel bildirim tipleri (sayfa yenilense de silinmesin)
const LOCAL_BANNER_TYPES = ['calendar', 'break', 'update'];

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [adminBellNotifications, setAdminBellNotifications] = useState([]);
  const { user, authToken, isAuthenticated } = useAuth();
  const pollingIntervalRef = useRef(null);
  const lastNotificationIdRef = useRef(null);
  const deletedNotificationIdsRef = useRef(new Set());
  const notificationPermissionAskedRef = useRef(false);
  // Race condition önleme: her kullanıcı oturumu için artan sayaç
  const loadGenerationRef = useRef(0);
  // Her zaman güncel user ID'yi tutan ref — closure'larda stale user?.id sorununu çözer
  const currentUserIdRef = useRef(user?.id);
  currentUserIdRef.current = user?.id;
  // Her zaman güncel notifications'ı tutan ref (stale closure sorununu önler)
  const notificationsRef = useRef(notifications);
  notificationsRef.current = notifications;

  // Kullanıcı değişince (login/logout/hesap değiştirme) bildirimleri sıfırla
  useEffect(() => {
    if (user?.id) {
      loadGenerationRef.current += 1;
      const stored = getNotificationsFromStorage(user.id);
      setNotifications(stored);
      setAdminBellNotifications(getAdminBellFromStorage(user.id));
      deletedNotificationIdsRef.current = new Set();
      lastNotificationIdRef.current = null;
    } else {
      loadGenerationRef.current += 1;
      setNotifications([]);
      setAdminBellNotifications([]);
    }
  }, [user?.id]);

  // Logout event'ini dinle — hem silent hem non-silent logout'u kapsar
  // currentUserIdRef kullanarak eski kullanıcının localStorage key'ini de temizler
  useEffect(() => {
    const handleLogout = () => {
      loadGenerationRef.current += 1;
      // Eski kullanıcının localStorage key'ini temizle (user null olmadan önce ref'ten okuyoruz)
      const oldUserId = currentUserIdRef.current;
      if (oldUserId) {
        saveNotificationsToStorage([], oldUserId);
      }
      setNotifications([]);
      setAdminBellNotifications([]);
      deletedNotificationIdsRef.current = new Set();
      lastNotificationIdRef.current = null;
    };
    window.addEventListener('notesplus-user-logged-out', handleLogout);
    return () => window.removeEventListener('notesplus-user-logged-out', handleLogout);
  }, []);

  // Notification API permission: login sonrası bir kez iste (reminder + daily summary için)
  useEffect(() => {
    if (!isAuthenticated) return;
    if (notificationPermissionAskedRef.current) return;
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (Notification.permission !== 'default') return;
    notificationPermissionAskedRef.current = true;
    Notification.requestPermission().catch(() => {});
  }, [isAuthenticated]);

  // Load notifications from backend
  const loadNotifications = useCallback(async () => {
    if (!isAuthenticated || !authToken) {
      setNotifications([]);
      return;
    }

    // Bu isteğin başladığı anki generation'ı kaydet
    const myGeneration = loadGenerationRef.current;

    try {
      const result = await notificationApi.getNotifications({ token: authToken });

      // await dönerken kullanıcı değişmiş olabilir — stale response ise yoksay
      if (loadGenerationRef.current !== myGeneration) return;

      if (result?.ok && result?.notifications) {
        const formatted = result.notifications
          .map(notif => {
            const rendered = renderNotification(notif);
            return {
              id: notif.id,
              type: notif.type || 'info',
              title: rendered.title || notif.title || i18n.t('notifications:genericTitle'),
              message: rendered.message || notif.message || '',
              timestamp: notif.createdAt,
              read: notif.isRead,
              data: notif.data || null,
            };
          })
          .filter(notif => !deletedNotificationIdsRef.current.has(notif.id));

        const apiIds = new Set(formatted.map((n) => n.id));
        const fromStorage = getNotificationsFromStorage(currentUserIdRef.current);
        const localBanners = fromStorage.filter(
          (n) =>
            LOCAL_BANNER_TYPES.includes(n.type) &&
            !apiIds.has(n.id) &&
            !deletedNotificationIdsRef.current.has(n.id)
        );
        const merged = [...formatted, ...localBanners].sort(
          (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
        );

        // Check if there's a new notification (for showing toast)
        const latestNotification = formatted[0];
        if (latestNotification && latestNotification.id !== lastNotificationIdRef.current) {
          lastNotificationIdRef.current = latestNotification.id;
          if (latestNotification.type === 'success' || latestNotification.type === 'error') {
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
                detail: {
                  type: latestNotification.type,
                  title: latestNotification.title,
                  message: latestNotification.message,
                },
              }));
            }
          }
        }

        setNotifications(merged);
      }
    } catch (error) {
      // Stale response ise yoksay
      if (loadGenerationRef.current !== myGeneration) return;
      // Offline-first: Server bağlantı hatalarını sessizce yönet
      const loadedNotifications = getNotificationsFromStorage(currentUserIdRef.current);
      setNotifications(loadedNotifications);
    }
  }, [isAuthenticated, authToken]);

  useEffect(() => {
    // Initial load
    loadNotifications();

    // Poll for new notifications every 10 seconds
    if (isAuthenticated && authToken) {
      pollingIntervalRef.current = setInterval(() => {
        loadNotifications();
      }, 10000); // Poll every 10 seconds

      return () => {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      };
    }
  }, [loadNotifications, isAuthenticated, authToken]);

  // Daily 00:01 calendar summary: show banner "Bugün X adet etkinliğiniz mevcut"
  useEffect(() => {
    const handler = (e) => {
      const count = e?.detail?.count ?? 0;
      if (count > 0 && typeof addNotificationRef.current === 'function') {
        addNotificationRef.current({
          type: 'calendar',
          title: i18n.t('shared:calendarContext.dailySummaryTitle'),
          message: i18n.t('shared:calendarContext.dailySummaryMessage', { count }),
        });
      }
    };
    window.addEventListener('notesplus-daily-calendar-summary', handler);
    return () => window.removeEventListener('notesplus-daily-calendar-summary', handler);
  }, []);

  // State her değiştiğinde localStorage'a yaz (render-phase side-effect olmadan)
  useEffect(() => {
    saveNotificationsToStorage(notifications, currentUserIdRef.current);
  }, [notifications]);

  const addNotificationRef = useRef(null);
  // Bildirim ekle
  const addNotification = useCallback((notification) => {
    const newNotification = {
      id: Date.now().toString(),
      type: notification.type || 'info', // 'calendar', 'break', 'info'
      title: notification.title || i18n.t('notifications:genericTitle'),
      message: notification.message || '',
      timestamp: new Date().toISOString(),
      read: false,
      data: notification.data || null, // Ek bilgiler (event, breakItem vb.)
    };

    setNotifications((prevNotifications) => {
      const updatedNotifications = [newNotification, ...prevNotifications];
      // Storage yazımı useEffect ile yapılıyor (render phase side-effect kaçınmak için)
      return updatedNotifications;
    });
    return newNotification;
  }, []);
  addNotificationRef.current = addNotification;

  // Bildirimi okundu olarak işaretle
  const markAsRead = useCallback(async (notificationId) => {
    setNotifications((prev) => prev.map((n) =>
      n.id === notificationId ? { ...n, read: true } : n
    ));

    if (isAuthenticated && authToken) {
      try {
        await notificationApi.markAsRead({ token: authToken, notificationId });
      } catch (error) {
        console.error('Mark notification as read error:', error);
      }
    }
  }, [isAuthenticated, authToken]);

  // Bildirimi sil
  const removeNotification = useCallback(async (notificationId) => {
    // Silinen bildirim ID'sini Set'e ekle (polling'de tekrar gelmesin diye)
    deletedNotificationIdsRef.current.add(notificationId);

    // notificationsRef ile güncel listeyi al (stale closure önlemi)
    const deletedNotif = notificationsRef.current.find((n) => n.id === notificationId);

    // Functional updater ile state'i güncelle (her zaman güncel prev alır)
    setNotifications((prev) => prev.filter((n) => n.id !== notificationId));

    // Yerel bildirimler (calendar, break) backend'de olmadığından API çağrısı yapma
    if (deletedNotif && LOCAL_BANNER_TYPES.includes(deletedNotif.type)) return;

    // Backend'e silme isteği gönder
    if (isAuthenticated && authToken) {
      try {
        await notificationApi.deleteNotification({ token: authToken, notificationId });
      } catch (error) {
        // Sessizce hata yönet — optimistic update geçerliliğini koruyor
      }
    }
  }, [isAuthenticated, authToken]);

  // Admin çan bildirimleri — localStorage'a yaz
  useEffect(() => {
    const key = getAdminBellStorageKey(currentUserIdRef.current);
    if (key) {
      try {
        localStorage.setItem(key, JSON.stringify(adminBellNotifications));
      } catch {}
    }
  }, [adminBellNotifications]);

  // Admin çan bildirimleri — socket event dinleyici
  useEffect(() => {
    if (!isAuthenticated || !authToken) return;
    const handler = async (e) => {
      const data = e.detail;
      if (!data?.id) return;
      setAdminBellNotifications((prev) => {
        if (prev.some((n) => n.id === data.id)) return prev; // Deduplicate
        return [{
          id: data.id,
          content: data.content || '',
          sentByName: data.sentByName || i18n.t('shared:authContext.roleLabels.admin'),
          link: data.link || null,
          timestamp: new Date().toISOString(),
          read: false,
        }, ...prev];
      });
      // Görüldü + teslim edildi işaretle
      try {
        await request(`/admin/notifications/${data.id}/seen`, { method: 'POST', token: authToken });
      } catch (_) {}
    };
    window.addEventListener('notesplus-admin-bell-notification', handler);
    return () => window.removeEventListener('notesplus-admin-bell-notification', handler);
  }, [isAuthenticated, authToken]);

  // Admin çan bildirimini sil (X butonu) — backend'e deleted_at kaydeder
  const removeAdminBellNotification = useCallback(async (id) => {
    setAdminBellNotifications((prev) => prev.filter((n) => n.id !== id));
    try {
      await request(`/admin/notifications/${id}/deleted`, { method: 'POST', token: authToken });
    } catch (_) {}
  }, [authToken]);

  // Admin çan bildirimini okundu işaretle
  const markAdminBellAsRead = useCallback((id) => {
    setAdminBellNotifications((prev) => prev.map((n) => n.id === id ? { ...n, read: true } : n));
  }, []);

  // Tüm bildirimleri temizle
  const clearAllNotifications = useCallback(async () => {
    const current = notificationsRef.current;

    // Tüm bildirim ID'lerini Set'e ekle (polling'de tekrar gelmesin diye)
    current.forEach(notif => {
      deletedNotificationIdsRef.current.add(notif.id);
    });

    // Optimistic update - UI'da hemen temizle (useEffect storage'ı güncelleyecek)
    setNotifications([]);

    // LOCAL_BANNER_TYPES dışındakileri backend'den sil
    const apiNotifs = current.filter(n => !LOCAL_BANNER_TYPES.includes(n.type));
    if (isAuthenticated && authToken && apiNotifs.length > 0) {
      const deletePromises = apiNotifs.map(notif =>
        notificationApi.deleteNotification({ token: authToken, notificationId: notif.id })
          .catch(error => {
            console.error(`Delete notification ${notif.id} error:`, error);
            deletedNotificationIdsRef.current.delete(notif.id);
            return { ok: false, error };
          })
      );

      const results = await Promise.all(deletePromises);

      // Başarısız olan bildirimleri geri ekle (rollback)
      const failedNotifications = apiNotifs.filter((_, index) => !results[index]?.ok);
      if (failedNotifications.length > 0) {
        setNotifications(failedNotifications);
      }
    }
  }, [isAuthenticated, authToken]);

  // Okunmamış bildirim sayısı (admin çan bildirimleri dahil)
  const unreadCount = notifications.filter((n) => !n.read).length
    + adminBellNotifications.filter((n) => !n.read).length;

  const value = {
    notifications,
    addNotification,
    markAsRead,
    removeNotification,
    clearAllNotifications,
    unreadCount,
    adminBellNotifications,
    removeAdminBellNotification,
    markAdminBellAsRead,
  };

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

